<template>
	<div class="row bank-flow-assign">
		<div class="col-12">
			<!-- Le mouvement tel que la banque le rapporte, présenté comme sur le parcours fournisseur :
			     c'est le même mouvement, il n'y a pas de raison de le lire différemment. -->
			<div class="bank-flow-assign__header">
				<h3>{{ amount }}</h3>
				<!-- Surligner une partie du libellé la reprend dans la recherche : c'est là qu'apparaît
				     le nom du tiers ou une référence de facture. -->
				<p class="mb-1" @mouseup="captureSelection">{{ bridge_transaction.bridgetransaction_description }}</p>
				<div class="bank-flow-assign__label">{{ date }}</div>
				<div class="mt-2">
					<img v-if="bank_logo_url" :src="bank_logo_url" class="mr-1" style="height: 20px;">{{ bank_name }}
				</div>
			</div>

			<h4>{{ $t('invoice.bank_flow_justificatif') }}</h4>

			<form class="form-group" @submit.prevent="search">
				<div class="input-group">
					<input type="text" class="form-control" v-model="search_value" :placeholder="$t('invoice.search_invoice_tiers')">
					<div class="input-group-append">
						<button class="btn btn-secondary" type="submit" :disabled="loading_invoices">
							<font-awesome-icon v-if="loading_invoices" :icon="['fal', 'spinner']" spin />
							<font-awesome-icon v-else :icon="['fal', 'search']" />
						</button>
					</div>
				</div>
			</form>

			<!-- Panier d'affectation : les pièces retenues survivent aux recherches successives. Sans lui,
			     chercher la deuxième facture faisait perdre la première, et rien ne le disait. -->
			<div v-if="basket.length" class="bank-flow-assign__basket">
				<div class="bank-flow-assign__count">{{ $t('invoice.bank_flow_basket', { count: basket.length }) }}</div>

				<div v-for="(item, key) in basket" :key="'basket_' + key" class="bank-flow-assign__card">
					<div class="d-flex align-items-start">
						<div class="flex-grow-1">
							<div class="font-weight-bold">{{ item.checkdeposit_ref || item.tiers_rs }}</div>
							<div v-if="item.num" class="bank-flow-assign__label">{{ item.num }} - {{ item.date }}</div>
							<div class="bank-flow-assign__label">{{ $t('invoice.invoice_balance') }} : {{ item.balance_formatted || item.amount }}</div>

							<!-- Montant imputé à cette facture. La somme des factures retenues dépasse
							     souvent le mouvement : c'est l'opérateur qui dit ce qui va où. -->
							<div class="input-group input-group-sm mt-1">
								<input
									type="text"
									class="form-control"
									:value="allocations[itemKey(item)]"
									@input="setAllocation(item, $event.target.value)"
								>
								<div class="input-group-append">
									<span class="input-group-text">{{ currency_symbol }}</span>
								</div>
							</div>
						</div>
						<button class="btn btn-link p-0 ml-2" :title="$t('invoice.bank_flow_basket_remove')" @click="toggleBasket(item)">
							<font-awesome-icon :icon="['far', 'times']" />
						</button>
					</div>
				</div>

				<!-- Le total ventilé doit valoir le mouvement au centime près : en deçà, le reliquat
				     partirait au compte courant sans que rien ne le dise ; au-delà, on imputerait plus
				     que la banque n'a versé. -->
				<div class="bank-flow-assign__count" :class="{ 'text-danger': !allocation_balanced }">
					{{ $t('invoice.bank_flow_basket_allocated') }} : {{ allocated_format }} / {{ amount }}
				</div>

				<button class="btn btn-primary btn-block" :disabled="!can_save" @click="checkForm()">
					{{ $t('invoice.associer') }}
					<font-awesome-icon v-if="processing" :icon="['fal', 'spinner']" pulse />
				</button>
			</div>

			<LoadingSpinner v-if="loading_invoices" />

			<template v-else>
				<p v-if="!invoices.length">{{ $t('invoice.invoices_search_empty') }}</p>

				<!-- Les propositions sont rangées par nature : régler une facture crée une écriture,
				     rattacher un règlement déjà saisi n'en crée aucune, et une remise de chèques en
				     rapproche tout un lot. Mêlées, elles se ressemblaient à s'y méprendre. -->
				<template v-for="family in families">
					<div v-if="family.items.length" :key="family.code" class="bank-flow-assign__count">
						{{ $t(family.label, { count: family.items.length }) }}
					</div>

					<!-- Une case à cocher et non deux boutons : ici plusieurs pièces se règlent d'un même
					     mouvement, et c'est leur somme qui doit tomber juste. -->
					<div v-for="(invoice, key) in family.items" :key="family.code + '_' + key" class="bank-flow-assign__card">
						<!-- Pièce qui se rapproche d'un seul geste : une remise ou un compte courant, que
						     l'affectation refuse de combiner, ou une proposition, dont le montant tombe
						     déjà juste. Une case à cocher y promettrait une composition sans objet. -->
						<template v-if="family.single">
							<div class="font-weight-bold">
								<font-awesome-icon v-if="invoice.checkdeposit_id" :icon="['far', 'money-check-edit-alt']" v-b-tooltip.hover="$t('check.check_deposit')" />
								<font-awesome-icon v-else-if="invoice.current_account_id" :icon="['far', 'hands-usd']" v-b-tooltip.hover="$t('payment.compte_courant')" />
								<font-awesome-icon v-else-if="invoice.payment_id" :icon="['far', 'file-invoice']" v-b-tooltip.hover="$t('payment.payment')" />
								<router-link
									v-if="invoice.tiers_id"
									:to="{ name: 'tiersFiche', params: { tiers_id: invoice.tiers_id }}"
								>{{ invoice.tiers_rs }}</router-link>
								<template v-else>{{ invoice.checkdeposit_ref || invoice.tiers_rs }}</template>
							</div>
							<div v-if="invoice.num" class="bank-flow-assign__label">
								<a v-if="invoice.id" href="" @click.prevent="quickPreview(invoice)">{{ invoice.num }} <font-awesome-icon :icon="['fas', 'eye']" /></a>
								<template v-else>{{ invoice.num }}</template>
							</div>
							<div class="bank-flow-assign__label">{{ invoice.date }}</div>
							<div class="font-weight-bold">
								{{ invoice.amount }}
								<template v-if="invoice.balance"> - {{ $t('invoice.invoice_balance') }} : {{ invoice.balance_formatted }}</template>
							</div>

							<button class="btn btn-primary btn-block mt-2" :disabled="processing" @click="assignSingle(invoice)">
								{{ $t('invoice.associer') }}
								<font-awesome-icon v-if="processing" :icon="['fal', 'spinner']" pulse />
							</button>
						</template>

						<b-form-checkbox v-else name="invoices" :checked="inBasket(invoice)" @change="toggleBasket(invoice)">
							<span class="font-weight-bold">
								<font-awesome-icon v-if="invoice.payment_id" :icon="['far', 'file-invoice']" v-b-tooltip.hover="$t('payment.payment')" />
								<!-- Le clic sur le tiers ne doit pas cocher la case qui l'enveloppe : on part
								     consulter sa fiche, on ne choisit pas la pièce. -->
								<router-link
									v-if="invoice.tiers_id"
									:to="{ name: 'tiersFiche', params: { tiers_id: invoice.tiers_id }}"
									@click.native.stop
								>{{ invoice.tiers_rs }}</router-link>
								<template v-else>{{ invoice.tiers_rs }}</template>
							</span>

							<div class="bank-flow-assign__label">
								<a href="" @click.prevent="quickPreview(invoice)">{{ invoice.num }} <font-awesome-icon :icon="['fas', 'eye']" /></a>
								- {{ invoice.date }}
							</div>

							<div class="font-weight-bold">
								{{ invoice.amount }}
								<template v-if="invoice.balance"> - {{ $t('invoice.invoice_balance') }} : {{ invoice.balance_formatted }}</template>
							</div>
						</b-form-checkbox>
					</div>
				</template>

				<a v-if="searched" href="" class="d-block text-center mt-3" @click.prevent="init_component">{{ $t('invoice.bank_flow_supplier_search_clear') }}</a>
			</template>
		</div>

        <b-modal size="xl" ref="modelPreview" hide-footer @hidden="pdf_url = null; base64 = null">
            <template v-slot:modal-title>
                {{ $t("action.previsualiser") }}
            </template>

            <iframe v-if="pdf_url != null" :src="pdf_url" height="1000px" width="100%" style="position:relative"></iframe>
            <iframe v-else-if="base64 != null" :src="'data:application/pdf;base64,' + base64" height="1000px" width="100%" style="position:relative"></iframe>
            <div v-else>
                <LoadingSpinner />
            </div>
        </b-modal>
	</div>
</template>

<script type="text/javascript">
    import Invoice from "@/mixins/Invoice.js"
    import OpenBank from "@/mixins/OpenBank.js"
    import Payment from "@/mixins/Payment.js"

	export default {
		name: 'AssignationTransaction',
		mixins: [Invoice, OpenBank, Payment],
		props: {
			bridge_transaction: { type: Object, required: true },
			// Extrait du libellé surligné dans la liste avant d'ouvrir le rapprochement.
			initial_search: { type: String, default: '' }
		},
		data () {
			return {
				amount: '',
				date: '',
				search_value: '',
				invoices: [],
				loading_invoices: false,
				// Vrai dès qu'une recherche a remplacé les propositions par ses résultats.
				searched: false,
				// Pièces retenues pour ce mouvement. Tenues à part des résultats affichés, qu'une
				// recherche remplace : la sélection, elle, doit traverser les recherches successives.
				basket: [],
				// Montant imputé à chaque pièce du panier, en unités, saisi par l'utilisateur.
				allocations: {},
				allocated_format: '',
				processing: false,
				pdf_url: null,
				base64: null,
			}
		},

		mounted() {
			this.init_component()
		},

		methods: {
			async init_component() {
				this.loading_invoices = true
				this.amount = await this.priceFormat(this.bridge_transaction.bridgetransaction_amount, this.bridge_transaction.bridgetransaction_currency, true)
				this.date = this.formatDate(this.bridge_transaction.bridgetransaction_date)

				const invoices = await this.getPotentialsInvoicesByBridgeTransaction(this.bridge_transaction.bridgetransaction_id)
				for(let i in invoices) {
					invoices[i].amount = await this.priceFormat(invoices[i].amount/100, invoices[i].currency, true)
					invoices[i].balance_formatted = invoices[i].balance ? await this.priceFormat(invoices[i].balance/100, invoices[i].currency, true) : null
					invoices[i].date = this.formatDate(invoices[i].date)
				}
				this.invoices = invoices
				this.searched = false
				this.basket = []
				this.allocations = {}
				this.loading_invoices = false

				// Le surlignage fait dans la liste vaut demande de recherche.
				if(this.initial_search) {
					this.search_value = this.initial_search
					this.search()
				}
			},

			async search() {
				this.loading_invoices = true
				const invoices = await this.searchInvoiceBridgeTransaction(this.bridge_transaction.bridgetransaction_id, false, this.search_value);
				for(let i in invoices) {
					invoices[i].amount = await this.priceFormat(invoices[i].amount/100, invoices[i].currency, true)
					invoices[i].balance_formatted = invoices[i].balance ? await this.priceFormat(invoices[i].balance/100, invoices[i].currency, true) : null
					invoices[i].date = this.formatDate(invoices[i].date)
				}
				this.invoices = invoices
				this.searched = true
				this.loading_invoices = false
			},

			/**
			 * Rapproche une pièce qui ne se combine à aucune autre. La sélection est remise à plat avant
			 * d'être posée sur elle : une case restée cochée ailleurs ferait refuser l'affectation.
			 *
			 * @param {Object} item Remise de chèques ou compte courant retenu.
			 */
			async assignSingle(item) {
				await this.checkForm([item])
			},

			/**
			 * Clé stable d'une pièce, toutes natures confondues : c'est elle qui dit si deux résultats
			 * de recherches différentes désignent le même objet.
			 *
			 * @param {Object} item
			 * @return {String}
			 */
			/**
			 * Lit un montant saisi à la main. La virgule décimale vaut le point : c'est ainsi qu'on écrit
			 * un montant en français.
			 *
			 * @param {String} value
			 * @return {Number}
			 */
			parseAmount(value) {
				const parsed = parseFloat(String(value || '0').replace(',', '.'))

				return isNaN(parsed) ? 0 : parsed
			},

			itemKey(item) {
				if(item.checkdeposit_id) return 'checkdeposit_' + item.checkdeposit_id
				if(item.payment_id) return 'payment_' + item.payment_id
				if(item.current_account_id) return 'current_account_' + item.current_account_id

				return 'invoice_' + item.id
			},

			inBasket(item) {
				return this.basket.some(retained => this.itemKey(retained) === this.itemKey(item))
			},

			/**
			 * Retient ou retire une pièce du panier, puis recalcule la somme retenue.
			 *
			 * @param {Object} item
			 */
			toggleBasket(item) {
				const key = this.itemKey(item)

				if(this.inBasket(item)) {
					this.basket = this.basket.filter(retained => this.itemKey(retained) !== key)
					this.$delete(this.allocations, key)
				}
				else {
					this.basket.push(item)
					// La pièce entre avec ce qu'il reste à imputer, plafonné à ce qu'elle doit : c'est la
					// répartition attendue neuf fois sur dix, et l'utilisateur n'a qu'à corriger le reste.
					this.$set(this.allocations, key, this.defaultAllocation(item))
				}

				this.formatAllocated()
			},

			/**
			 * Part proposée à une pièce qui entre au panier : ce qui reste du mouvement, sans dépasser
			 * ce que la pièce doit encore.
			 *
			 * @param {Object} item
			 * @return {String} Montant en unités, à deux décimales.
			 */
			defaultAllocation(item) {
				const remaining = this.amount_cents - this.allocated_cents
				const due = Math.abs(item.balance || item.amount_cents || 0)
				const proposed = Math.min(Math.abs(remaining), due) * Math.sign(remaining || 1)

				return (proposed / 100).toFixed(2)
			},

			/**
			 * Retient le montant saisi pour une pièce. La virgule décimale est acceptée : c'est ainsi
			 * qu'on écrit un montant en français, et le refuser silencieusement laisserait un total faux.
			 *
			 * @param {Object} item
			 * @param {String} value
			 */
			setAllocation(item, value) {
				this.$set(this.allocations, this.itemKey(item), value)
				this.formatAllocated()
			},

			async formatAllocated() {
				this.allocated_format = await this.priceFormat(this.allocated_cents / 100, this.bridge_transaction.bridgetransaction_currency, true)
			},

			/**
			 * Rapproche le mouvement des pièces retenues.
			 *
			 * @param {Array} items Pièces à affecter. Le panier par défaut ; une pièce seule quand elle
			 *                      ne se combine à rien.
			 */
			async checkForm(items) {
				this.processing = true
				const invoices = items || this.basket
				if(invoices.length == 0) {
					this.processing = false
					return false
				}

				const invoices_ids = invoices.filter(invoice => invoice.id).map(invoice => invoice.id)
				const checkdeposit_ids = invoices.filter(invoice =>  invoice.checkdeposit_id).map(invoice => invoice.checkdeposit_id)
				const payment_ids = invoices.filter(invoice =>  invoice.payment_id).map(invoice => invoice.payment_id)
				const current_account_ids = invoices.filter(invoice =>  invoice.current_account_id).map(invoice => invoice.current_account_id)

				if((checkdeposit_ids.length > 1 || (checkdeposit_ids.length == 1 && invoices_ids.length > 0)) || (current_account_ids.length > 1 || (current_account_ids.length == 1 && invoices_ids.length > 0))) {
					this.processing = false
					return false
				}

				const checkdeposit_id = checkdeposit_ids.length == 0 ? '' : checkdeposit_ids[0]
				const payment_id = payment_ids.length == 0 ? '' : payment_ids[0]
				let current_account_id = current_account_ids.length == 0 ? '' : current_account_ids[0]
				if(current_account_id == 'new') {
					let cc = invoices.filter(invoice =>  invoice.current_account_id)[0]
					current_account_id += "_"+cc.tiers_id+"_"+cc.accountingplan_id
				}

				// La répartition n'accompagne que les factures : une remise ou un compte courant se
				// rapproche en entier, il n'y a rien à ventiler.
				const allocations = {}
				invoices.filter(invoice => invoice.id).forEach(invoice => {
					allocations[invoice.id] = Math.round(this.parseAmount(this.allocations[this.itemKey(invoice)]) * 100)
				})

				await this.assignBridgeTransaction(this.bridge_transaction.bridgetransaction_id, false, invoices_ids, checkdeposit_id, payment_id, current_account_id, '', allocations)
				this.processing = false
				// Le mouvement est rapproché : il ne se réaffecte plus, et la page qui nous porte décide
				// de la suite.
				this.$emit('done')
			},

			/**
			 * Cherche sur ce que l'utilisateur vient de surligner dans le libellé : surligner un nom de
			 * commerçant ou une référence, c'est déjà demander la facture qui va avec.
			 */
			captureSelection() {
				const selection = window.getSelection().toString().trim()

				if(selection) {
					this.search_value = selection
					this.search()
				}
			},

            async quickPreview(invoice) {
            	this.pdf_url = null
            	this.base64 = null
            	
                this.$refs.modelPreview.show()
            	if(invoice.pdf_url) {
            		this.pdf_url = invoice.pdf_url
            	}
            	else if(invoice.checkdeposit_id) {
            		let checkdeposit_ids = {checkdeposit_ids: invoice.checkdeposit_id.toString()}
            		let retour = await this.printCheckDeposit(checkdeposit_ids, true)
            		if(retour) {
	                    this.base64 = retour
	                }
            	}
            	else {
	                let retour = await this.pdfInvoice([invoice.id], "", true)
	                if(retour) {
	                    this.base64 = retour
	                }
            	}
            }
		},

		computed: {
			/**
			 * Propositions rangées par nature d'écriture, dans l'ordre où on les traite le plus souvent.
			 * Une famille vide ne s'affiche pas.
			 *
			 * @return {Array} Familles {code, label, items}.
			 */
			families() {
				return [
					{
						code: 'invoice',
						label: 'invoice.bank_flow_client_invoices',
						// Une proposition tombe déjà sur le montant du mouvement : il n'y a rien à
						// composer, un clic suffit. La case à cocher ne sert qu'après une recherche,
						// où l'utilisateur réunit plusieurs pièces pour atteindre la somme.
						single: !this.searched,
						items: this.invoices.filter(i => !i.payment_id && !i.checkdeposit_id && !i.current_account_id)
					},
					{
						code: 'payment',
						label: 'invoice.bank_flow_client_payments',
						single: !this.searched,
						items: this.invoices.filter(i => i.payment_id)
					},
					{
						code: 'check_deposit',
						label: 'invoice.bank_flow_client_check_deposits',
						single: true,
						items: this.invoices.filter(i => i.checkdeposit_id)
					},
					{
						code: 'current_account',
						label: 'invoice.bank_flow_client_current_accounts',
						single: true,
						items: this.invoices.filter(i => i.current_account_id)
					}
				]
			},

			bank_name() {
				const account = this.bridge_transaction.bridge_account
				return account ? account.bridge_bank.bridgebank_name + ' - ' + account.bridgeaccount_name : ''
			},

			bank_logo_url() {
				const account = this.bridge_transaction.bridge_account
				return account ? account.bridge_bank.bridgebank_logo_url : null
			},

			/**
			 * Montant du mouvement en centimes, dans la convention des soldes de facture.
			 */
			amount_cents() {
				return Math.round(this.bridge_transaction.bridgetransaction_amount * 100)
			},

			currency_symbol() {
				return this.bridge_transaction.bridgetransaction_currency === 'EUR' ? '€' : this.bridge_transaction.bridgetransaction_currency
			},

			allocated_cents() {
				return this.basket.reduce((total, item) => {
					return total + Math.round(this.parseAmount(this.allocations[this.itemKey(item)]) * 100)
				}, 0)
			},

			allocation_balanced() {
				return this.allocated_cents === this.amount_cents
			},

			can_save() {
				if(this.basket.length === 0 || this.processing) {
					return false
				}

				// Une remise ou un compte courant se rapproche seul et en entier : il n'y a rien à
				// ventiler, donc rien à faire tomber juste.
				const invoices = this.basket.filter(item => item.id)

				return invoices.length === 0 || this.allocation_balanced
			}
		},

		components: {
			LoadingSpinner: () => import('GroomyRoot/components/Logos/LoadingSpinner_35')
		}
	}

</script>